import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Drawer, Select } from '@saas-kit/ui';

import type { QmsToken } from './types';
import type { WorkstationState } from './useWorkstation';

interface Props {
  open: boolean;
  token: QmsToken;
  ws: WorkstationState;
  onClose: () => void;
}

/** Hand the counter's current token to another claimed, idle counter. */
export function SendToCounterDialog({
  open,
  token,
  ws,
  onClose,
}: Props): JSX.Element {
  const { t } = useTranslation('qms');
  const [counterId, setCounterId] = useState('');
  const here = ws.session?.counter_id ?? null;
  const options = ws.counters.filter((c) => c.id !== here);

  return (
    <Drawer
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={t('workstation.send.title')}
      footer={
        <Button
          disabled={!counterId || ws.sendToCounter.isPending}
          onClick={() =>
            ws.sendToCounter.mutate(
              { tokenId: token.id, counterId },
              { onSuccess: onClose },
            )
          }
        >
          {t('workstation.send.submit')}
        </Button>
      }
    >
      {options.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {t('workstation.send.empty')}
        </p>
      ) : (
        <label className="block space-y-1 text-sm">
          <span className="text-foreground">
            {t('workstation.send.field')}
          </span>
          <Select
            value={counterId}
            onChange={(e) => setCounterId(e.target.value)}
          >
            <option value="">—</option>
            {options.map((counter) => (
              <option key={counter.id} value={counter.id}>
                {counter.name}
              </option>
            ))}
          </Select>
        </label>
      )}
    </Drawer>
  );
}
